import { getFaceCenter } from "./faceDetection";
import { drawTemplateTexts } from "./drawTemplateTexts";
import {
    extractTextContent,
    AuthFetch,
} from "./extractTextContent";
import { buildExtractionFields } from "./buildExtractionFields";
import { splitPresentationTitle } from "./splitTitle";
import { Template } from "./types";
import { TextContent } from "./types";

const loadImage = (
    src: string
): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();

        img.crossOrigin = "anonymous";

        img.onload = () =>
            resolve(img);

        img.onerror = () =>
            reject(
                new Error(
                    `Bild konnte nicht geladen werden: ${src}`
                )
            );

        img.src = src;
    });

function drawImageCoverOnFace(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    faceCenter: {
        x: number;
        y: number;
    } | null
) {
    const imgRatio =
        img.width / img.height;

    const targetRatio =
        width / height;

    let sw = img.width;
    let sh = img.height;

    if (imgRatio > targetRatio) {
        sw =
            img.height *
            targetRatio;
    } else {
        sh =
            img.width /
            targetRatio;
    }

    const centerX =
        faceCenter?.x ??
        img.width / 2;

    const centerY =
        faceCenter?.y ??
        img.height / 2;

    let sx =
        centerX -
        sw / 2;

    let sy =
        centerY -
        sh / 2;

    sx = Math.max(
        0,
        Math.min(
            sx,
            img.width - sw
        )
    );

    sy = Math.max(
        0,
        Math.min(
            sy,
            img.height - sh
        )
    );

    ctx.drawImage(
        img,
        sx,
        sy,
        sw,
        sh,
        x,
        y,
        width,
        height
    );
}

/**
 * Generiert ein fertiges Template-Bild aus einer Datei + Text.
 * Im Gegensatz zur Logik in ImageTransformer läuft dies auf einem
 * losgelösten Offscreen-Canvas (kein DOM-Ref nötig) und ist damit
 * sowohl in der Komponente selbst als auch für Batch-Exporte
 * (z. B. PostPlanBuilder) nutzbar.
 */
export async function generateTemplateImage(
    file: File | null,
    text: string,
    template: Template,
    apiFetch: AuthFetch
): Promise<string> {
    const canvas =
        document.createElement(
            "canvas"
        );

    const ctx =
        canvas.getContext(
            "2d"
        );

    if (!ctx) {
        throw new Error(
            "Canvas-Context konnte nicht erstellt werden."
        );
    }

    const templateImg =
        await loadImage(
            template.src
        );

    let uploadedImg:
        HTMLImageElement | null =
        null;

    if (
        template.imageArea != null
    ) {
        if (!file) {
            throw new Error(
                "Dieses Template benötigt ein Bild, aber es wurde keins übergeben."
            );
        }

        uploadedImg =
            await loadImage(
                URL.createObjectURL(
                    file
                )
            );
    }

    const extractionFields =
        buildExtractionFields(
            template.textAreas
        );

    let textContents: TextContent =
        {} as TextContent;

    if (extractionFields.length > 0) {
        const rawContents =
            await extractTextContent(
                text,
                extractionFields,
                apiFetch
            );

        textContents = {
            ...rawContents,
        };

        if (
            rawContents.presentation_title
        ) {
            const {
                part1,
                part2,
            } =
                splitPresentationTitle(
                    rawContents.presentation_title
                );

            textContents.presentation_title_part_1 =
                part1;

            textContents.presentation_title_part_2 =
                part2;
        }
    }

    canvas.width =
        templateImg.width;

    canvas.height =
        templateImg.height;

    if (
        template.imageArea != null
    ) {
        const {
            x,
            y,
            width,
            height,
        } =
            template.imageArea;

        ctx.fillStyle =
            template.backgroundcolor;

        ctx.fillRect(
            x,
            y,
            width,
            height
        );
    }

    if (
        template.imageArea != null &&
        uploadedImg
    ) {
        const {
            x,
            y,
            width,
            height,
        } =
            template.imageArea;

        const faceCenter =
            await getFaceCenter(
                uploadedImg
            );

        drawImageCoverOnFace(
            ctx,
            uploadedImg,
            x,
            y,
            width,
            height,
            faceCenter
        );
    }

    ctx.drawImage(
        templateImg,
        0,
        0
    );

    await drawTemplateTexts(
        ctx,
        template.textAreas,
        textContents
    );

    return canvas.toDataURL(
        "image/png"
    );
}